import { clamp01 } from '../../shared/scalar';
import { hash } from '../hash';

const TAU = Math.PI * 2;

export const UNIT = 40;

export interface Spot {
  readonly x: number;
  readonly y: number;
}

interface Orbit {
  readonly reach: number;
  readonly turnsPerBeat: number;
  readonly phase: number;
  readonly tilt: number;
  readonly lift: number;
}

export const ORBITERS = 28;
const ORBITS: readonly Orbit[] = Array.from({ length: ORBITERS }, (_, index) => ({
  reach: 64 + hash(index, 1) * 28,
  turnsPerBeat: (0.05 + hash(index, 2) * 0.05) * (index % 2 === 0 ? 1 : -1),
  phase: hash(index, 3) * TAU,
  tilt: (hash(index, 4) - 0.5) * 0.5,
  lift: hash(index, 6) * 12,
}));
const FLAT = 0.42;
const STILL: Orbit = { reach: 64, turnsPerBeat: 0, phase: 0, tilt: 0, lift: 0 };

export function orbit(index: number, beats: number): Spot & { readonly front: boolean } {
  const { reach, turnsPerBeat, phase, tilt, lift } = ORBITS[index] ?? STILL;
  const angle = phase + beats * turnsPerBeat * TAU;
  const x = Math.cos(angle) * reach;
  return { x, y: Math.sin(angle) * reach * FLAT + x * tilt - lift, front: Math.sin(angle) > 0 };
}

const THRESHOLDS: readonly number[] = Array.from({ length: ORBITERS }, (_, index) =>
  hash(index, 5),
);

export function density(hp: number, maxHp: number): number {
  return 0.3 + 0.7 * clamp01(hp / maxHp);
}

export function alive(index: number, share: number): boolean {
  return (THRESHOLDS[index] ?? 1) <= share;
}

export function jitter(index: number, beats: number, amp: number): Spot {
  return {
    x: Math.sin(beats * 3.1 + index * 1.7) * amp,
    y: Math.cos(beats * 2.7 + index * 2.3) * amp,
  };
}

export const FLOAT = { live: 3, calm: 1 };

export function wingFrame(index: number, beats: number, calm: boolean, frames: number): number {
  if (calm) {
    return Math.floor(frames / 2);
  }
  const beat = Math.sin(beats * 40 + index) * 0.5 + 0.5;
  return Math.min(frames - 1, Math.floor(beat * frames));
}

export const SHOVE = { beats: 0.8, reach: 30, push: 10 };

export function shove(dx: number, dy: number, sinceBeats: number): Spot {
  if (sinceBeats < 0 || sinceBeats > SHOVE.beats) {
    return { x: 0, y: 0 };
  }
  const far = Math.hypot(dx, dy) || 1;
  const k = SHOVE.push * Math.exp(-sinceBeats * 5) * Math.exp(-far / SHOVE.reach);
  return { x: (dx / far) * k, y: (dy / far) * k };
}

const PART = { reach: 34, give: 0.9 };

export function part(dx: number, dy: number): Spot {
  const far = Math.hypot(dx, dy);
  if (far >= PART.reach) {
    return { x: 0, y: 0 };
  }
  const away = far === 0 ? { x: 1, y: 0 } : { x: dx / far, y: dy / far };
  const k = (PART.reach - far) * PART.give;
  return { x: away.x * k, y: away.y * k };
}

const SPARK_BEATS = 0.7;

export interface Spark {
  readonly glow: number;
  readonly ring: number;
  readonly alpha: number;
}

export function spark(sinceBeats: number): Spark | undefined {
  if (sinceBeats < 0 || sinceBeats >= SPARK_BEATS) {
    return undefined;
  }
  return {
    glow: 2 + sinceBeats * 3,
    ring: 2 + sinceBeats * 10,
    alpha: 1 - sinceBeats / SPARK_BEATS,
  };
}

export const TRAIL = 7;
const TRAIL_LAG = 0.025;
const TRAIL_RISE = 40;

export function trailSpot(index: number, p: number): { out: number; arc: number; sway: number } {
  const q = clamp01(p - index * TRAIL_LAG);
  const there = q < 0.5 ? (2 * q) ** 2 : 1 - (2 * q - 1) ** 2;
  return {
    out: there,
    arc: Math.sin(there * Math.PI) * TRAIL_RISE,
    sway: Math.sin(q * 14 + index) * 3,
  };
}

const TARGET = { wide: 1.6, throb: 0.12, landBeats: 0.7 };

export interface Target {
  readonly radius: number;
  readonly fill: number;
  readonly ring?: { readonly radius: number; readonly alpha: number };
}

export function target(sinceBeats: number, flightBeats: number): Target | undefined {
  if (sinceBeats < 0) {
    return undefined;
  }
  if (sinceBeats < flightBeats) {
    const p = sinceBeats / flightBeats;
    const throb = Math.exp(-(sinceBeats % 1) * 5);
    return { radius: TARGET.wide + (1 - TARGET.wide) * p, fill: 0.12 + TARGET.throb * throb };
  }
  const since = sinceBeats - flightBeats;
  if (since >= TARGET.landBeats) {
    return undefined;
  }
  const a = since / TARGET.landBeats;
  return { radius: 1, fill: 0.24 * (1 - a), ring: { radius: 1 + a * 0.6, alpha: 1 - a } };
}

export const FORM_BEATS = 4;

export function forming(index: number, count: number, sinceBeats: number): number {
  return clamp01((sinceBeats / FORM_BEATS - (index / count) * 0.5) / 0.5);
}

export function formSway(p: number, index: number): number {
  return Math.sin(p * 8 + index) * 8 * (1 - p);
}

export const POP = { beats: 1.4, spread: 0.6, rate: 1.6 };

export function pop(index: number, sinceBurst: number): Spark | 'gone' | undefined {
  const a = (sinceBurst / POP.beats - (index / ORBITERS) * POP.spread) * POP.rate;
  if (a <= 0) {
    return undefined;
  }
  return a >= 1 ? 'gone' : { glow: 2 + a * 3, ring: 2 + a * 12, alpha: 1 - a };
}
